"use client";

// import FullPageLoader from "@/components/Loader";
import { c as _c } from "react/compiler-runtime";
import { findDevice } from "@/lib/utils/app-utils";
import { pushToDataLayer } from "@/lib/utils/gtm-utils";
import axios from "axios";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { nanoid } from "nanoid";
const EventContext = createContext();
const MAX_PENDING_EVENTS = 100;
const useEvent = () => {
  const $ = _c(2);
  const context = useContext(EventContext);
  let t0;
  if ($[0] !== context) {
    t0 = {
      ...context
    };
    $[0] = context;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const EventProvider = ({
  children
}) => {
  const [clevertapModule, setClevertapModule] = useState(null);
  const [userId, setUserId] = useState(null);
  // Refs, not state: pushEvent must see init progress even from a render that
  // captured it before CleverTap finished loading.
  const clevertapRef = useRef(null);
  const initStatusRef = useRef("idle"); // idle | loading | ready | failed
  const pendingEventsRef = useRef([]);
  // const [initialAppLoad, setInitialAppLoad] = useState(true);

  useEffect(() => {
    // Initialize user ID on first visit
    initializeUserId();

    // Initialize CleverTap in the background without blocking rendering
    clevertapInit();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initializeUserId = () => {
    // Ensure we're in the browser environment
    if (typeof window === "undefined") {
      return;
    }
    try {
      // Check if user ID already exists in localStorage
      const storedUserId = localStorage.getItem("userId");
      if (storedUserId) {
        // User ID exists, use it
        setUserId(storedUserId);
      } else {
        // First visit - generate a new unique ID (12 characters)
        const newUserId = nanoid(6);
        localStorage.setItem("userId", newUserId);
        setUserId(newUserId);
      }
    } catch (error) {
      // Handle localStorage errors (e.g., in private browsing mode)
      console.error("Failed to initialize user ID:", error);
    }
  };
  const getUserId = () => {
    // First try to get from state
    if (userId) {
      return userId;
    }

    // Fallback to localStorage if state is not yet initialized
    if (typeof window !== "undefined") {
      try {
        const storedUserId = localStorage.getItem("userId");
        if (storedUserId) {
          return storedUserId;
        }
      } catch (error) {
        // localStorage not available
        console.error("Failed to get user ID:", error);
      }
    }
    return null;
  };
  const clevertapInit = async () => {
    if (initStatusRef.current !== "idle") return;
    initStatusRef.current = "loading";
    try {
      const clevertap = await initializeClevertap();
      clevertapRef.current = clevertap;
      initStatusRef.current = "ready";
      const currentUserId = getUserId();
      const eventData = {
        Source: "Search",
        Platform: findDevice(),
        ...(currentUserId && {
          userId: currentUserId
        })
      };

      // Only push event after initialization
      clevertap.event.push("website_opened", eventData);

      // Events pushed while the SDK was still loading (already sent to GTM).
      pendingEventsRef.current.forEach(({
        event,
        eventData: data
      }) => clevertap.event.push(event, data));
      pendingEventsRef.current = [];
      setClevertapModule(clevertap);

      // Also push to GTM
      pushToDataLayer("website_opened", eventData);
    } catch (error) {
      initStatusRef.current = "failed";
      pendingEventsRef.current = [];
      console.log(error);
    }
  };

  // `includeUserId: false` for flows with no login (the anonymous id is not a user identity there).
  const pushEvent = (event, props = {}, {
    includeUserId = true
  } = {}) => {
    const currentUserId = includeUserId ? getUserId() : null;
    const eventData = {
      ...props,
      Platform: findDevice(),
      ...(currentUserId && {
        userId: currentUserId
      })
    };

    // Push to CleverTap — queued until init finishes so early events
    // (page views fire right on load) aren't lost to CleverTap.
    if (clevertapRef.current) {
      clevertapRef.current.event.push(event, eventData);
    } else if (initStatusRef.current !== "failed" && pendingEventsRef.current.length < MAX_PENDING_EVENTS) {
      pendingEventsRef.current.push({
        event,
        eventData
      });
    }

    // Also push to GTM dataLayer
    pushToDataLayer(event, eventData);
  };
  const uploadEventToClevertap = ({
    identity,
    eventName,
    eventData = {}
  }) => {
    const clevertapUploadUrl = process.env.NEXT_PUBLIC_CLEVERTAP_EVENT_BASE_URL;

    // Validate that the base URL is configured
    if (!clevertapUploadUrl) {
      console.warn("NEXT_PUBLIC_CLEVERTAP_EVENT_BASE_URL is not configured. Skipping event upload.", {
        eventName,
        identity
      });
      return;
    }

    // Ensure the URL doesn't end with a slash to avoid double slashes
    const baseUrl = clevertapUploadUrl.replace(/\/$/, "");
    const uploadUrl = `${baseUrl}/api/v1/upload-event`;
    const currentUserId = getUserId();
    const enrichedEventData = {
      ...eventData,
      ...(currentUserId && {
        userId: currentUserId
      })
    };
    axios.post(uploadUrl, {
      identity,
      eventName,
      eventData: enrichedEventData
    }).catch(error => {
      // Silently handle errors to prevent breaking the app
      console.error("Failed to upload event to CleverTap:", error);
    });
  };

  // Show loading screen for initial app load only
  // if (initialAppLoad) {
  //   return <FullPageLoader />;
  // }

  // After initial load, always render children and continue CleverTap init in background
  return <EventContext.Provider value={{
    pushEvent,
    uploadEventToClevertap,
    isAnalyticsReady: Boolean(clevertapModule),
    userId
  }}>
      {children}
    </EventContext.Provider>;
};
export { EventProvider, useEvent };
async function initializeClevertap() {
  const clevertapModule = await import("clevertap-web-sdk");
  clevertapModule.default.init(process.env.NEXT_PUBLIC_CLEVERTAP_KEY);
  clevertapModule.default.spa = true;
  clevertapModule.default.privacy.push({
    optOut: false
  });
  clevertapModule.default.privacy.push({
    useIP: false
  });
  clevertapModule.default.setLogLevel(3);
  return clevertapModule.default;
}